import { useQuery } from '@tanstack/react-query';
import { ms } from 'ms';
import { ENDPOINT } from '~/constants';

import { KyRequestOptions } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { i18n } from '~/utils';

import { queryKeysAssetReparsing } from './queryKeys';

/**
 * Load original DLN file submitted to our AI parser.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin/operation/get_reparsing_original_dln_file_vadmin_asset_reparsing_original_dln_file_get
 */
const getOriginalDlnFile = async (
  filename: string,
  options?: KyRequestOptions,
) => {
  try {
    // Fetch the file as Blob.
    const blob = await vestigasApi
      .get(ENDPOINT.ASSET_REPARSING.GET_ORIGINAL_DLN_FILE(filename), options)
      .blob();

    // Convert Blob to File
    const file = new File([blob], filename, { type: 'application/pdf' });

    return file;
  } catch (error) {
    // Attach custom message to error object.
    const customError = new Error(
      i18n.t(($) => $.assetReparsing.query.originalDlnFile.error.generic),
    );

    console.error('Error fetching original DLN file:', error);

    // Re-throw the error to propagate it to the calling function.
    throw customError;
  }
};

/**
 * A custom hook to fetch an original DLN file submitted to our AI parser.
 * Uses the `useQuery` hook from `@tanstack/react-query` internally.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin/operation/get_reparsing_original_dln_file_vadmin_asset_reparsing_original_dln_file_get
 *
 * @param {string} originalDlnFileName - The name of the original DLN file to fetch.
 * @return {UseQueryResult<File>} The result of the query, which is the fetched (PDF) file.
 */
export const useQueryOriginalDlnFile = (originalDlnFileName: string) =>
  useQuery({
    enabled: Boolean(originalDlnFileName),
    gcTime: ms('10m'),
    queryFn: async ({ signal }) =>
      getOriginalDlnFile(originalDlnFileName, { signal }),
    queryKey: queryKeysAssetReparsing.getDlnFile(originalDlnFileName),
    staleTime: Infinity, // Don't refetch a cached file as this leads to an annoying flicker in the PDF viewer,
  });
